import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, assetUrl } from '../api/index.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useLocale } from '../contexts/LocaleContext.jsx';

const POLL_INTERVAL = 5000; // 5 seconds

export default function Conversation() {
  const { userId } = useParams();
  const { user } = useAuth();
  const { t } = useLocale();
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [otherUser, setOtherUser] = useState(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);

  async function loadMessages() {
    try {
      const data = await api.messages.history(userId);
      setMessages(data.messages ?? []);
    } catch {
      // ignore poll errors silently
    }
  }

  useEffect(() => {
    // Load other user's profile
    api.users.getById(userId).then(setOtherUser).catch(() => navigate('/messages'));

    // Initial message load
    loadMessages().finally(() => setLoading(false));

    // Start polling
    pollRef.current = setInterval(loadMessages, POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function handleSend(e) {
    e.preventDefault();
    const body = input.trim();
    if (!body || sending) return;

    setSending(true);
    const optimistic = {
      id: `opt-${Date.now()}`,
      sender_id: user?.sub ?? user?.id,
      recipient_id: userId,
      body,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    setInput('');

    try {
      await api.messages.send({ recipient_id: userId, body });
      // Next poll will replace the optimistic message
    } catch (err) {
      alert(err.message);
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
    } finally {
      setSending(false);
    }
  }

  const myId = user?.sub ?? user?.id;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-brand-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-4 flex flex-col h-[calc(100vh-4rem)]">
      {/* Header */}
      <div className="flex items-center gap-3 pb-3 border-b border-gray-200 mb-2 shrink-0">
        <button onClick={() => navigate('/messages')} className="text-gray-400 hover:text-gray-700 mr-1">
          ←
        </button>
        {otherUser?.avatar_url ? (
          <img src={assetUrl(otherUser.avatar_url)} alt={otherUser.display_name} className="w-9 h-9 rounded-full object-cover" />
        ) : (
          <div className="w-9 h-9 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 font-bold text-sm">
            {otherUser?.display_name?.[0]?.toUpperCase()}
          </div>
        )}
        <div>
          <button
            onClick={() => navigate(`/${otherUser?.role === 'caddie' ? 'caddies' : 'golfers'}/${userId}`)}
            className="font-semibold text-gray-900 text-sm hover:text-brand-700"
          >
            {otherUser?.display_name}
          </button>
          <p className="text-xs text-gray-400">{otherUser?.role}</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-3 py-2">
        {messages.length === 0 && (
          <div className="text-center text-gray-400 text-sm pt-10">
            {t('startConversation')} 👋
          </div>
        )}
        {messages.map((m) => {
          const isMe = m.sender_id === myId;
          return (
            <div key={m.id} className={`flex items-end gap-2 ${isMe ? 'justify-end' : 'justify-start'}`}>
              {!isMe && (
                otherUser?.avatar_url ? (
                  <img src={assetUrl(otherUser.avatar_url)} alt="" className="h-7 w-7 rounded-full object-cover" />
                ) : (
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-700">
                    {otherUser?.display_name?.[0]?.toUpperCase()}
                  </div>
                )
              )}
              <div
                className={[
                  'max-w-[75%] rounded-2xl px-4 py-2.5 text-sm',
                  isMe
                    ? 'bg-brand-700 text-white rounded-br-sm'
                    : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm shadow-sm',
                ].join(' ')}
              >
                <p>{m.body}</p>
                <p className={`text-xs mt-1 ${isMe ? 'text-brand-200' : 'text-gray-400'}`}>
                  {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
              {isMe && (
                user?.avatar_url ? (
                  <img src={assetUrl(user.avatar_url)} alt="" className="h-7 w-7 rounded-full object-cover" />
                ) : (
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-200 text-xs font-bold text-gray-600">
                    {user?.display_name?.[0]?.toUpperCase()}
                  </div>
                )
              )}
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="flex gap-2 pt-3 border-t border-gray-200 shrink-0">
        <input
          className="input flex-1"
          placeholder={t('typeMessage')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={sending}
          autoFocus
        />
        <button type="submit" disabled={sending || !input.trim()} className="btn-primary px-5">
          {sending ? '…' : t('send')}
        </button>
      </form>
    </div>
  );
}
